import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { toArabicDigits } from '../../../utils/numbers';
import Icon from '../../../components/AppIcon';

const EngagementChart = ({ stats }) => {
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover rounded-lg p-3">
          {payload?.map((entry, index) => (
            <p key={index} className="text-xs text-muted-foreground">
              {entry?.name}: <span className="font-bold text-foreground">{toArabicDigits(entry?.value)}</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const views = stats?.find((s) => s.icon === 'Eye')?.value ?? 0;
  const claps = stats?.find((s) => s.icon === 'Heart')?.value ?? 0;
  const comments = stats?.find((s) => s.icon === 'MessageCircle')?.value ?? 0;
  const chartData = [{ period: '', views, claps, comments }];
  const hasAnyNonZero = (views || 0) + (claps || 0) + (comments || 0) > 0;

  const maxEngagement = Math.max(views, claps, comments, 1);
  const maxDigitCount = String(Math.ceil(maxEngagement)).length;
  const yAxisWidth = Math.max(50, 35 + maxDigitCount * 10);

  const CustomYAxisTick = ({ x, y, payload }) => {
    const label = toArabicDigits(payload?.value);
    const digitCount = Math.max(1, String(payload?.value ?? '').length);
    const dx = -(8 + (digitCount - 1) * 7);
    return (
      <g transform={`translate(${x},${y})`}>
        <text textAnchor="end" fill="var(--color-muted-foreground)" fontFamily="Amiri" fontSize={12} dx={dx}>
          {label}
        </text>
      </g>
    );
  };

  if (!hasAnyNonZero) {
    return (
      <div className="bg-card rounded-lg p-4 md:p-6 shadow-warm">
        <h3 className="text-base md:text-lg lg:text-xl font-bold text-foreground mb-4 md:mb-6">
          تحليل التفاعل
        </h3>
        <div className="w-full h-64 md:h-80 lg:h-96 flex flex-col items-center justify-center text-center">
          <Icon name="BarChart3" size={48} className="text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4" style={{ fontFamily: 'Amiri, serif' }}>
            لا توجد بيانات تفاعل بعد
          </p>
          <p className="text-sm text-muted-foreground max-w-md" style={{ fontFamily: 'Amiri, serif' }}>
            انشر مقالاتك وشاركها مع القراء لتبدأ رؤية إحصائيات المشاهدات والإعجابات والتعليقات هنا
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg p-4 md:p-6 shadow-warm">
      <h3 className="text-base md:text-lg lg:text-xl font-bold text-foreground mb-4 md:mb-6">
        تحليل التفاعل
      </h3>
      <div className="w-full h-64 md:h-80 lg:h-96" aria-label="مخطط التفاعل">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 5, right: 5, left: 8, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="period" 
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px' }}
              tick={{ fill: 'transparent' }}
            />
            <YAxis 
              width={yAxisWidth}
              tickMargin={12}
              stroke="var(--color-muted-foreground)"
              tick={<CustomYAxisTick />}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ fontSize: '12px' }}
              iconType="circle"
              content={({ payload }) => (
                <ul className="recharts-default-legend" style={{ padding: 0, margin: 0, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '12px 20px' }}>
                  {payload?.map((entry, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: entry.color
                        }}
                      />
                      <span style={{ color: entry.color }}>{entry.value}</span>
                    </li>
                  ))}
                </ul>
              )}
            />
            <Bar 
              dataKey="views" 
              fill="#4a6b4a" 
              name="المشاهدات"
              radius={[8, 8, 0, 0]}
            />
            <Bar 
              dataKey="claps" 
              fill="#C5A059" 
              name="الإعجابات"
              radius={[8, 8, 0, 0]}
            />
            <Bar 
              dataKey="comments" 
              fill="var(--color-success)" 
              name="التعليقات"
              radius={[8, 8, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default EngagementChart;